// src/pages/Auth/InviteResetPassword.jsx
import { useEffect, useState,useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import BaseCard from "../../../components/Cards/Card";
import CustomPasswordInput from "../../../components/PasswordInput";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useFormik } from "formik";
import { toast, Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { clearInviteCredentials } from "../../../Redux/Slices/AuthSlice";
import { BASE_URL } from "../../../components/BaseUrl";

const RuleRow = ({ ok, label, showError }) => {
  const textColor = ok
    ? '#16a34a'   // green if rule passed
    : showError
      ? '#d32f2f' // red if failed AND user clicked continue
      : '#64748b';// gray by default

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: ok ? 'none' : '2px solid #9aa4b2',
          background: ok ? '#22c55e' : 'transparent'
        }}
      >
        {ok ? (
          <span
            style={{
              width: 6,
              height: 10,
              borderRight: '2px solid #fff',
              borderBottom: '2px solid #fff',
              transform: 'rotate(45deg)',
            }}
          />
        ) : (
          <span style={{ position: 'relative', width: 10, height: 10 }}>
            <span
              style={{
                position: 'absolute',
                width: '100%',
                height: 2,
                background: showError ? '#d32f2f' : '#9aa4b2',
                transform: 'rotate(45deg)',
                top: '50%',
                left: 0,
                marginTop: -1,
              }}
            />
            <span
              style={{
                position: 'absolute',
                width: '100%',
                height: 2,
                background: showError ? '#d32f2f' : '#9aa4b2',
                transform: 'rotate(-45deg)',
                top: '50%',
                left: 0,
                marginTop: -1,
              }}
            />
          </span>
        )}
      </span>
      <span style={{ fontSize: 14, color: textColor }}>{label}</span>
    </Box>
  );
};
const PasswordChecklist = ({ password, showError }) => {
    const { t } = useTranslation();
  const rules = useMemo(() => ({
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(password),
  }), [password]);

  return (
   <Box sx={{ mt: 1, mb: { xs: 2, sm: 1.5 } }}>
  <RuleRow ok={rules.length} label={t('passwordRules.minLength')} showError={showError && !rules.length}/>
  <RuleRow ok={rules.upper} label={t('passwordRules.upperCase')} showError={showError && !rules.upper}/>
  <RuleRow ok={rules.lower} label={t('passwordRules.lowerCase')} showError={showError && !rules.lower}/>
  <RuleRow ok={rules.number} label={t('passwordRules.number')} showError={showError && !rules.number}/>
  <RuleRow ok={rules.special} label={t('passwordRules.special')} showError={showError && !rules.special}/>
</Box>
  );
};

const InviteResetPassword = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // ✅ Only read token, no user/email
  const inviteTokenFromRedux = useSelector((s) => s.auth.inviteProfileUpdateToken);
  const inviteProfileUpdateToken =
    inviteTokenFromRedux || localStorage.getItem("inviteProfileUpdateToken");
const inviteEmailFromRedux = useSelector((s) => s.auth.inviteEmail);
const inviteEmail = inviteEmailFromRedux || localStorage.getItem("inviteEmail");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("invitePasswordCompleted") === "true") {
      navigate("/", { replace: true });
      return;
    }
    if (!inviteProfileUpdateToken) {
      toast.error("Token expired or missing. Please verify your invite again.");
      navigate("/company-admin/verify-invite", { replace: true });
    }
  }, [inviteProfileUpdateToken, navigate]);

  const validate = (values) => {
    const errors = {};
    if (!values.email) {
errors.email = t("resetPassword.errors.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email =     t("resetPassword.errors.emailInvalid");
;
    }
    if (!values.password) {
      errors.password =t("resetPassword.errors.passwordRequired");
    } else if (values.password.length < 8) {
      errors.password = t("resetPassword.errors.passwordLength");
    } else if (
      !/(?=.*[a-z])/.test(values.password) ||
      !/(?=.*[A-Z])/.test(values.password) ||
      !/(?=.*[!@#$%^&*])/.test(values.password)
    ) {
    errors.password = t("resetPassword.errors.passwordWeak");
    }

    if (!values.confirmPassword) {
 errors.confirmPassword = t("resetPassword.errors.confirmPasswordRequired");
    } else if (values.confirmPassword !== values.password) {
    errors.confirmPassword = t("resetPassword.errors.confirmPasswordMismatch");
    }

    return errors;
  };

  const formik = useFormik({
     enableReinitialize: true, 
    initialValues: {
         email: inviteEmail || "",

      password: "",
      confirmPassword: "",
    },
    validate,
    onSubmit: async (values) => {
      const errors = validate(values);
      if (Object.keys(errors).length > 0) {
        Object.values(errors).forEach((err) => toast.error(err));
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await fetch(`${BASE_URL}/company-admins/set-initial-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: values.email,
            verificationToken: inviteProfileUpdateToken,
            newPassword: values.password,
            confirmPassword: values.confirmPassword,
          }),
        });

        const data = await response.json();

        if (!response.ok || data.error) {
          throw new Error(data.message || "Failed to reset password");
        }

      toast.success(data.message || "Password has been set successfully", {
  id: "reset-success",   // unique ID
});
        dispatch(clearInviteCredentials());
        sessionStorage.setItem("invitePasswordCompleted", "true");
        setTimeout(() => {
          navigate("/admin-approval", { replace: true });
        }, 1200);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setIsSubmitting(false);
      }
    },
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  return (
    <Box sx={{ backgroundColor: "#EFF4F8", minHeight: "100vh" ,mt:isMobile?2:0,px:1,}}>

      <Box sx={{ py: isMobile ? 5 : 10, px:isMobile?0: 2, display: "flex", justifyContent: "center" ,alignItems:"center"}}>
        <BaseCard
          maxWidth={isMobile ? "100%" : 380}
          sx={{
            width: isMobile ? "100%" : "auto",
            boxSizing: "border-box",
            px: isMobile ? 0 : 0,
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
                <CustomText sx={{ ...textStyles.h4, color: theme.palette.tertiary.main }}>
                  {t("resetPassword.title", "Set Your Password")}
                </CustomText>
                <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary }}>
                  {t("resetPassword.subtitle", "Enter your email and a strong password")}
                </CustomText>
              </Box>

              {/* Email */}
              <label>{t("resetPassword.emailLabel")} <span style={{ color: "red" }}>*</span></label>
              <input
                type="email"
                name="email"
                  disabled 
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className='project_input'
                style={{
                  height:"42px"
                }}
              />
              {formik.touched.email && formik.errors.email && (
                <span style={{ color: "red", fontSize: "12px" }}>{formik.errors.email}</span>
              )}

              {/* Password */}
              <label style={{ marginTop: "10px" }}>  {t("resetPassword.passwordLabel")}<span style={{ color: "red" }}>*</span></label>
              <CustomPasswordInput
                name="password"
                placeholder='*********'
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && formik.errors.password}
                initiallyVisible={true}
              />
              {formik.touched.password && formik.errors.password && (
                <span style={{ color: "red", fontSize: "12px" }}>{formik.errors.password}</span>
              )}

              {/* Confirm Password */}
              <label style={{ marginTop: "10px" }}> {t("resetPassword.confirmPasswordLabel")}<span style={{ color: "red" }}>*</span></label>
              <CustomPasswordInput
                name="confirmPassword"
                     placeholder='*********'
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && formik.errors.confirmPassword}
                initiallyVisible={true}
              />
              {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                <span style={{ color: "red", fontSize: "12px" }}>{formik.errors.confirmPassword}</span>
              )}
              <PasswordChecklist password={formik.values.password} showError={formik.touched.password} />
  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0, mb: 1 }}>
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptedTerms}
                  onChange={e => setAcceptedTerms(e.target.checked)}
                  style={{ marginRight: 8, width: 18, height: 18 }}
                  required
                />
                <span
  style={{
 color: "#1976D2",
    cursor: "pointer",
    textDecoration: "underline",
    fontSize: 14,
  }}
  onClick={() => window.open("https://biometricpro.app/terms", "_blank")}
>
  {t("termsAndConditionsText") || "I accept the Terms and Conditions"}
</span>

              </Box>
              <CustomButton
                label={
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {isSubmitting && <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />}
                 {t("resetPassword.submitBtn")}
                  </Box>
                }
                type="submit"
                fullWidth
                                                disabled={!acceptedTerms || isSubmitting}

                sx={{ mt: 2 }}
              />
            </Box>
          </form>
        </BaseCard>
      </Box>
    </Box>
  );
};

export default InviteResetPassword;


